import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { truckingBillAPI } from '../../api/waybillAPI';
import useCompanyContext from '../../hooks/useCompanyContext';
import { useAuth } from '../../context/AuthContext';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-PH') : '—';
const PAGE_SIZE = 20;

function StatusBadge({ status }) {
  const map = {
    draft:          'bg-gray-100 text-gray-600',
    sent:           'bg-blue-100 text-blue-700',
    paid:           'bg-green-100 text-green-700',
    partially_paid: 'bg-yellow-100 text-yellow-700',
    overdue:        'bg-red-100 text-red-700',
    voided:         'bg-red-100 text-red-600 line-through',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.draft}`}>
      {status ? status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Draft'}
    </span>
  );
}

export default function FFBillingListPage() {
  const navigate = useNavigate();
  const { accountingClientId, contextKind } = useCompanyContext();
  const { user } = useAuth();
  const isBillingFF = user?.role === 'billing_ff';

  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const buildParams = useCallback((overrides = {}) => ({
    billTypeId: 1,
    ...(search      && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(dateFrom    && { dateFrom }),
    ...(dateTo      && { dateTo }),
    ...overrides,
  }), [search, statusFilter, dateFrom, dateTo]);

  // Reset to page 1 whenever filters or company context change
  useEffect(() => { setPage(1); }, [search, statusFilter, dateFrom, dateTo, accountingClientId, contextKind]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setBills([]);
    truckingBillAPI.getAll(buildParams({ page, limit: PAGE_SIZE }))
      .then((r) => {
        if (cancelled) return;
        const data = r.data;
        if (data && typeof data === 'object' && 'bills' in data) {
          setBills(data.bills || []);
          setTotal(data.total || 0);
        } else {
          setBills(Array.isArray(data) ? data : []);
          setTotal(Array.isArray(data) ? data.length : 0);
        }
      })
      .catch(() => { if (!cancelled) setBills([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, buildParams, accountingClientId, contextKind]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await truckingBillAPI.getAll(buildParams({ export: 'true' }));
      const data = Array.isArray(r.data) ? r.data : (r.data?.bills || []);
      exportCSV(data);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const exportCSV = (data) => {
    const headers = ['Bill No.', 'Ref No.', 'Customer', 'Waybill Serial No.', 'Bill Date', 'Due Date', 'Received', 'Status'];
    const rows = data.map((b) => [
      b.BillName || '',
      b.referenceNumber || '',
      b.WayBillId?.CustomerId?.CustomerName || b.WayBillId?.CustomerId?.name || '',
      b.WayBillId?.SerialNo || '',
      b.BillDate ? new Date(b.BillDate).toLocaleDateString('en-PH') : '',
      b.BillDueDate ? new Date(b.BillDueDate).toLocaleDateString('en-PH') : '',
      b.IsReceived ? 'Received' : 'Pending',
      (b.status || 'draft').replace(/_/g, ' '),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ff-billing-${dateFrom || 'all'}-to-${dateTo || 'records'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout pageTitle="FF Billing — Freight Forwarding">
      <div className="space-y-4">

        {/* ── Filters ── */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-64">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search bill no., ref, carrier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All Statuses</option>
            {['draft', 'sent', 'paid', 'partially_paid', 'overdue', 'voided'].map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
            ))}
          </select>

          <div className="flex items-center gap-1">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <span className="text-gray-400 text-sm">–</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            {!isBillingFF && (
              <button
                onClick={() => navigate('/billing/ff-billing/add')}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700"
              >
                <PlusIcon className="h-4 w-4" />
                New FF Bill
              </button>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Bill No.', 'Ref No.', 'Customer', 'Waybill Serial No.', 'Bill Date', 'Due Date', 'Received', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : bills.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                    No FF bills found.{' '}
                    {!isBillingFF && (
                      <button onClick={() => navigate('/billing/ff-billing/add')} className="text-teal-600 underline">
                        Create one.
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                bills.map((b) => (
                  <tr key={b._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{b.BillName}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{b.referenceNumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {b.WayBillId?.CustomerId?.CustomerName || b.WayBillId?.CustomerId?.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{b.WayBillId?.SerialNo || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt(b.BillDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt(b.BillDueDate)}</td>
                    <td className="px-4 py-3">
                      {b.IsReceived ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Received</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/billing/ff-billing/${b._id}/edit`)}
                        className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {total} record{total !== 1 ? 's' : ''}
              {totalPages > 1 && ` · page ${page} of ${totalPages}`}
            </span>
            {totalPages > 1 && (
              <div className="flex gap-1">
                <button disabled={page <= 1} onClick={() => setPage(1)}
                  className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">«</button>
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">Prev</button>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">Next</button>
                <button disabled={page >= totalPages} onClick={() => setPage(totalPages)}
                  className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">»</button>
              </div>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
}
